import { ICrop } from "../crops"
import { ICropping } from "../fields"
import { CropId, GrassManagement, GrassManagementEngland, GrowingSeasonLength } from "../generated/enums"
import { param } from "../generated/labels"
import { IParam, ParamKey } from "../parameters"
import { isArrayEmpty } from "../utils/array"
import { isDate } from "../utils/utils"
import { attrs } from "./strings"

/// <reference path="./strings.ts" />

const names = attrs

export interface ICroppingEditContext {

    readonly scotland: boolean

    crop: ICrop

    getParameters(type: ParamKey): IParam[]

    target: ICropping

}

const grassManagementWithCuts: GrassManagementEngland[] = [
    GrassManagementEngland.Cut,
    GrassManagementEngland.CutGrazed,
    GrassManagementEngland.Dehydrated,
    GrassManagementEngland.DehydratedIrrigation,
]

const growingSeasonLengthScotland: GrowingSeasonLength[] = [
    GrowingSeasonLength.L60_90_seed,
    GrowingSeasonLength.L60_90_ware,
]

const growingSeasonLengthEngland: GrowingSeasonLength[] = [
    GrowingSeasonLength.L60_90,
]

type AttritubeKey = Extract<ParamKey,
    | "CropResidue"
    | "CropPurpose"
    | "BiomassLevel"
    | "NLeafAnalysis"
    | "GrowingSeasonLength"
    | "CropProtectionStatus"
    | "GrassManagementEngland"
    | "GrassManagementScotland"
    | "GrassCuts"
    | "GrassNCropGroup"
>

export interface CroppingAttrContext {

    readonly scotland: boolean
    readonly crop: ICrop

    getParameters(key: AttritubeKey): IParam[]

}

export class CroppingAttr {

    static getNames() {
        return [
            "TargetYield",
            "Residue",
            "GrassManagement",
            "GrassCuts",
            "GrowingSeasonLength",
            "ProtectionStatus",
            "NCropGroup",
            "StrawMulch",
            "Undersown",
            "BiomassLevel",
            "NLeafAnalysis",
            "Market",
            "TargetpH",
        ]
    }

    static defineAttrs(ctxt: CroppingAttrContext): CroppingAttr[] {

        const { scotland } = ctxt

        return [

            getResidue(ctxt),

            getGrowingSeasonLength(ctxt),

            getProtectionStatus(),

            getGrassManagement(scotland),

            (scotland ? null : getGrassCuts()),

            getGrassNCropGroup(scotland),

            getNewAttr("BiomassLevel"),

            getNewAttr("NLeafAnalysis", true),

            getPurpose(ctxt),

        ].filter(f => !!f)


    }

    parameters: IParam[]
    lastCrop: ICrop

    cropCondition: string

    visible: (c: Partial<ICropping>) => boolean

    private _grassManagementParameters: {
        Grass: IParam[],
        Others: IParam[]
    }

    getDefaultValue?: () => any

    validate?: (c: Partial<ICropping>, value) => string


    constructor(
        public readonly paramType: AttritubeKey,
        public readonly model: string,
        public readonly label: string,
        public readonly required?: boolean) {

    }

    isRequired(crop: ICrop, cropping: () => Partial<ICropping>): boolean {

        if (!crop) return false

        if ((!this.visible) || this.visible(cropping())) {
            return crop[this.cropCondition || this.model]
        }

        return false

    }

    getParameters(ctxt: CroppingAttrContext) {

        if (this.parameters) {
            return this.parameters
        }

        const type: AttritubeKey = this.paramType

        if (type == "GrassManagementEngland") {

            const crop = ctxt.crop

            if (!crop) {
                return null
            }

            if (!this._grassManagementParameters) {

                const parameters = ctxt.getParameters(type)

                this._grassManagementParameters = {
                    Grass: parameters.filter(p => p.value != GrassManagement.Seed),
                    Others: parameters
                }

            }

            const r = (crop.CropId == CropId.Grass ? this._grassManagementParameters.Grass : this._grassManagementParameters.Others)

            return r

        }

        return ctxt.getParameters(type)

    }

}


function getGrowingSeasonLength(ctxt: CroppingAttrContext) {

    const key = "GrowingSeasonLength"

    const f = new CroppingAttr(key, key, names.GrowingSeasonLength, true)

    const parameters = getGrowingSeasonLengthValues(ctxt.scotland, ctxt.getParameters(key))

    f.parameters = parameters

    return f
}

function getProtectionStatus() {

    const key = "CropProtectionStatus"

    const f = new CroppingAttr(key, "ProtectionStatus", names.ProtectionStatus, true)

    f.isRequired = (c: ICrop) => c?.ProtectionStatus

    return f
}

export function getGrowingSeasonLengthValues(scotland: boolean, values: IParam[] = param.GrowingSeasonLength()) {

    const excluded = scotland
        ? growingSeasonLengthEngland
        : growingSeasonLengthScotland

    values = values.filter(p => excluded.indexOf(p.value) < 0)

    return values

}


function getGrassNCropGroup(scotland: boolean) {

    if (!scotland) return null

    const f = new CroppingAttr("GrassNCropGroup", "NCropGroup", names.NCropGroup, true)

    f.cropCondition = "GrassManagement"
    f.visible = c => isDate(c.DestructionDate)

    f.validate = (c, value) => {

        if (c && value && (value < 100)) {
            const msg = `The current group value of <strong>${value}</strong> is not valid anymore. Please pick the correct value from the list above.`
            return msg
        }

    }

    return f

}

function getGrassManagement(scotland: boolean) {

    const paramType = (scotland ? "GrassManagementScotland" : "GrassManagementEngland")

    return new CroppingAttr(paramType, "GrassManagement", names.GrassManagement, true)

}

function getGrassCuts() {

    const f = getNewAttr("GrassCuts", true)

    f.visible = (c: ICropping) => checkGrassCutsSupport(c.GrassManagement)

    return f

}

export function checkGrassCutsSupport(mgmt: GrassManagement) {

    return grassManagementWithCuts.indexOf(<number>mgmt) >= 0

}

function getResidue(ctxt: CroppingAttrContext) {

    const f = new CroppingAttr("CropResidue", "Residue", names.Residue, true)

    f.getDefaultValue = () => {
        const crop = ctxt.crop
        return crop ? crop.Residue : null
    }

    return f

}

function getPurpose(ctxt: CroppingAttrContext) {

    const f = new CroppingAttr("CropPurpose", "Market", names.Purpose, true)

    f.visible = (c: Partial<ICropping>) => {
        const crop = c?.Crop
        return (crop && crop.Purposes && crop.Purposes.UserSelection)
    }

    f.isRequired = (crop: ICrop) => crop?.Purposes?.UserSelection

    f.getParameters = () => {

        if (f.parameters && (ctxt.crop == f.lastCrop)) {
            return f.parameters
        }

        const markets = ctxt.crop?.Purposes?.Purposes

        f.parameters = isArrayEmpty(markets)
            ? []
            : param.CropPurpose().filter(p => markets.indexOf(p.value) >= 0)

        f.lastCrop = ctxt.crop

        return f.parameters

    }

    return f

}

function getNewAttr(model: AttritubeKey, required = false) {
    const label = attrs[model]
    return new CroppingAttr(model, model, label, required)
}

